import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SendoutType, SendoutViewModel, TableColumns } from '../../../types';
import { Sendout, Template, Card } from '../../../types';
import { DateFnsModule } from 'ngx-date-fns';
import { SendoutStatusPipe } from '../../../pipes/SendoutStatus.pipe';
import { StoreService } from '../../../services/store/store.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sendout-table',
  standalone: true,
  imports: [CommonModule, DateFnsModule, SendoutStatusPipe],
  templateUrl: './sendout-table.component.html',
  styleUrl: './sendout-table.component.css',
})
export class SendoutTableComponent implements OnChanges, OnDestroy {
  @Input() columns: TableColumns[] = [];
  @Input() data: SendoutViewModel[] = [];
  @Input() emptyStateText: string = '';
  @Output() clickEvent = new EventEmitter();
  sortColumn: string = '';
  isDesc: boolean = false;

  readonly SendoutType = SendoutType;
  private storeLookup?: Subscription;

  constructor(private storeService: StoreService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data']) return;
    this.storeLookup?.unsubscribe();
    const rows = this.data;
    const ids = [...new Set(rows.flatMap(s => s.storeIds))];
    if (!ids.length) return;
    this.storeLookup = this.storeService.resolveExistingStores(ids).subscribe({
      next: stores => {
        rows.forEach(s => {
          s.stores = s.storeIds.map(id => stores.find(store => store.id === id))
            .filter(store => store != null);
        });
        this.cdr.markForCheck();
      },
      // Keep any existing labels on failure; a new input starts a fresh lookup.
      error: () => {},
    });
  }

  ngOnDestroy(): void {
    this.storeLookup?.unsubscribe();
  }

  sortData(column: TableColumns) {
    const { field } = column;

    if (this.sortColumn === field) {
      this.isDesc = !this.isDesc;
    } else {
      this.sortColumn = field;
      this.isDesc = true;
    }

    this.data.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      let comparison =
        typeof column.sort == 'boolean'
          ? this.compareValues(valueA, valueB)
          : column.sort(valueA, valueB);

      return this.isDesc ? -comparison : comparison;
    });
  }

  compareValues(valueA: unknown, valueB: unknown): number {
    if (valueA === null || valueA === undefined) return -1;
    if (valueB === null || valueB === undefined) return 1;

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB);
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return valueA - valueB;
    }

    if (valueA instanceof Date && valueB instanceof Date) {
      return valueA.getTime() - valueB.getTime();
    }

    return 0;
  }

  handleRowClick(row: any | Sendout | Template | Card) {
    this.clickEvent.emit(row);
  }
}
